/**
 * O provedor de contexto de tela que uma SUPERFÍCIE registra no motor de chat
 * (US-5 da spec-001 do `project-plataforma-por-chat`).
 *
 * ★ Este arquivo é dos COMPONENTES, nunca da API. Ele lê a nav-bar de cada
 * módulo (`secoes-do-modulo.js` → constantes de 15 módulos, `visible-modules`,
 * `back-office/permissions`, `controladoria-v2/routes.js`) para listar as abas
 * do módulo atual — e é exatamente esse grafo que `api/apiChat.js` não pode
 * arrastar: um único import inocente em qualquer desses arquivos que passe a
 * puxar um `.vue`/`@router` reabre o `ReferenceError: Cannot access 'APIChat'
 * before initialization` de 26/08/2026 (cabeçalho de `contexto-de-tela.js`).
 *
 * Por isso a divisão: `contexto-de-tela.js` é PURO (sem imports) e é o que a
 * API importa; este arquivo compõe o provedor por cima dele e só é importado
 * por `view-chat.vue` (via `provedores-do-api-chat.js`), pelas duas telas do
 * fee-fixo e por `ai-framework/views/agent-chat.vue`. Medido em 14/09/2026:
 * com o provedor dentro de `contexto-de-tela.js` o grafo estático da API ia de
 * 35 para 94 arquivos.
 */

import {
  secoesDoModulo,
  rotulosDasSecoes,
} from '@modules/aawz-intelligence-v2/ui-registry/tools-de-front/secoes-do-modulo'
import { provedorDeContextoDeExecucao } from './contexto-de-execucao'
import { montarContextoDeTela } from './contexto-de-tela'

/**
 * A chave de `filtros_da_tela` com os RÓTULOS das abas do módulo atual
 * (14/09/2026) — para o modelo acertar o `secao` do `navegar` de primeira
 * ("Leads", e não "funil"). A fonte é a MESMA do `navegar`
 * (`secoes-do-modulo.js`): o que o modelo lê aqui é exatamente o que o
 * handler aceita depois.
 */
export const CHAVE_SECOES_DO_MODULO = 'secoes_do_modulo'

/**
 * `filtros` com `secoes_do_modulo` acrescentada por ÚLTIMO — é acessório, e
 * a ordem é o que o corte no teto (`cortarFiltrosDaTela`) respeita: com filtros
 * grandes, a lista de abas é a primeira a ficar de fora.
 *
 * Só com `$router` na superfície: sem ele `secoesDoModulo` não tem como
 * conferir que a aba existe, e uma lista não conferida ensinaria abas mortas.
 * Chave já declarada pela tela nunca é sobrescrita.
 */
function comSecoesDoModulo(filtros, vm) {
  const rota = vm && vm.$route
  if (!rota || !rota.name || !vm.$router) return filtros
  const base = filtros && typeof filtros === 'object' ? filtros : {}
  if (Object.hasOwn(base, CHAVE_SECOES_DO_MODULO)) return filtros
  const rotulos = rotulosDasSecoes(
    secoesDoModulo(rota.name, provedorDeContextoDeExecucao(vm)())
  )
  if (!rotulos.length) return filtros
  return { ...base, [CHAVE_SECOES_DO_MODULO]: rotulos }
}

/**
 * O provedor que uma superfície registra no `created()`:
 *
 *   apiChat.setContextoDeTelaProvider(provedorDeContextoDeTela(this, NAMESPACE))
 *
 * Lê `$route` e o getter OPCIONAL `<ns>/filtrosDaTela` no momento do ENVIO,
 * nunca no registro: entre uma mensagem e a seguinte o usuário navega, e um
 * valor capturado no `created` descreveria a tela de onde ele veio.
 *
 * O namespace é o da superfície — o drawer do fee-fixo mandando os filtros do
 * CRM descreveria uma tela que o usuário não está vendo.
 *
 * Aos filtros da tela junta-se `secoes_do_modulo` (as abas do módulo atual —
 * ver `comSecoesDoModulo`), quando a superfície tem `$router`.
 *
 * @param {Object} vm              Instância Vue (tem `$route` e `$store`).
 * @param {string} storeNamespace  Namespace Vuex da superfície.
 * @returns {Function}             `() => ({rota_atual, filtros_da_tela})`
 */
export function provedorDeContextoDeTela(vm, storeNamespace) {
  return function() {
    const rota = vm && vm.$route
    const store = vm && vm.$store
    const getters = (store && store.getters) || {}
    return montarContextoDeTela({
      rota: rota,
      filtros: comSecoesDoModulo(getters[storeNamespace + '/filtrosDaTela'], vm),
    })
  }
}
